import React, { useMemo } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import ConfigurablePdfViewer from "components/PdfViewer/ConfigurablePdfViewer";
import { getLoggedInUser } from "services/AuthService";
import { SWITCH_DISPOSITION } from "rbac/RbacContentProvider";
import UsabilityContext from "lookup/UsabilityContext";
import { DEFAULT_RAW_FAX_PROGRAM_CODE } from "lookup/RawConstants";
import { WorkflowConstants } from "lookup/WorkflowConstants";
import FaxDispositionHeader, {
  RAW_STATUSES,
} from "components/FaxDisposition/FaxDispositionHeader";
import FaxDispositionNotes from "components/FaxDisposition/FaxDispositionNotes";
import FaxDispositionActions from "components/FaxDisposition/FaxDispositionActions";
import { getDispositionFieldComponent } from "components/FaxDisposition/dispositions";
import {
  FAX_DISPOSITION_PROGRAM_CODES,
  getActionsLayoutForContext,
  getFaxDispositionConfig,
  getUnlinkDeclinedFaxConfig,
  resolveFaxDispositionContext,
} from "./FaxDispositionConfig";
import {
  buildDispositionPayload,
  getSelectableDispositions,
  validateDisposition,
} from "./FaxDisposition.helpers";
import { useFaxDisposition } from "./useFaxDisposition";
import { getRawListRedirectPath } from "./faxDispositionUrlUtils";

const FaxDisposition = () => {
  const { rawId } = useParams();
  const location = useLocation();
  const listQueryParams = location.search || "";
  const queryParams = new URLSearchParams(listQueryParams);
  const faxProgramCode =
    queryParams.get("faxProgramCode") || DEFAULT_RAW_FAX_PROGRAM_CODE;

  const context = resolveFaxDispositionContext(location, faxProgramCode);
  const contextIsRaw = context === UsabilityContext.RAW;
  const config = getFaxDispositionConfig(faxProgramCode);
  const isSwitchProgram =
    faxProgramCode === FAX_DISPOSITION_PROGRAM_CODES.SWITCH;

  const changeDisposition = location.state?.changeDisposition ?? false;
  const isForAccepted = location.state?.isForAccepted ?? false;
  const isForDeclined = location.state?.isForDeclined ?? false;
  const isPrescriberResponseFaxAvailable =
    location.state?.isPrescriberResponseFaxAvailable ?? false;
  const isSubmissionRequestFromSwim =
    location.state?.isSubmissionRequestFromSwim ?? false;

  const {
    rawDetails,
    rawDetailsStatus,
    formValues,
    setFieldValue,
    resetDispositionFields,
    submittingDisposition,
    shouldExit,
    handleSubmitDisposition,
    handleSubmitPushToPharmacy,
    handleSubmitForReview,
    handleUnclaimRaw,
  } = useFaxDisposition({
    rawId,
    faxProgramCode,
    changeDisposition,
    contextIsRaw,
    isSubmissionRequestFromSwim,
  });

  const baseSelectableDispositions =
    SWITCH_DISPOSITION.provideSelectableDispositions(context);

  const selectableDispositions = useMemo(() => {
    if (!rawDetails) {
      return baseSelectableDispositions;
    }

    return getSelectableDispositions(rawDetails, baseSelectableDispositions, {
      changeDisposition,
      isForAccepted,
      isForDeclined,
      isPrescriberResponseFaxAvailable,
    });
  }, [
    rawDetails,
    baseSelectableDispositions,
    changeDisposition,
    isForAccepted,
    isForDeclined,
    isPrescriberResponseFaxAvailable,
  ]);

  if (rawDetailsStatus === "failure") {
    return "Failed to fetch RAW details";
  }

  if (rawDetailsStatus === "requested" || !rawDetails) {
    return "Fetching Response Assessment Workflow ... ";
  }

  if (shouldExit) {
    if (contextIsRaw && !isSubmissionRequestFromSwim) {
      return (
        <Navigate
          to={getRawListRedirectPath(listQueryParams, faxProgramCode)}
          replace
        />
      );
    }
    return null;
  }

  const loggedInUserId = getLoggedInUser().uuid;
  const isOwnedByLoggedInUser = rawDetails.ownerId === loggedInUserId;
  const isRawConfirmed = rawDetails.status === RAW_STATUSES.CONFIRMED;
  const disableActions =
    (!isOwnedByLoggedInUser && !changeDisposition) ||
    (isRawConfirmed && !changeDisposition) ||
    rawDetails.wfItemStatus === WorkflowConstants.TARGET_NOT_FILLED;

  const DispositionFields = getDispositionFieldComponent(
    config.dispositionFieldComponent
  );

  const actionsLayout = getActionsLayoutForContext(config, contextIsRaw);

  const submitDispositionHandler = (values = formValues) => {
    const payload = buildDispositionPayload(
      faxProgramCode,
      values,
      rawDetails
    );

    handleSubmitDisposition(payload, {
      validateBeforeSubmit: () =>
        validateDisposition(
          faxProgramCode,
          values,
          selectableDispositions,
          rawDetails
        ),
    });
  };

  const notesDisabled =
    isSwitchProgram &&
    formValues.disposition === selectableDispositions.DECLINED
      ? false
      : disableActions;

  const showNotes =
    !config.notes?.isVisible ||
    config.notes.isVisible({ rawDetails, changeDisposition });

  return (
    <Container fluid>
      <Row>
        <Col md={5}>
          <FaxDispositionHeader
            headerConfig={config.header}
            rawDetails={rawDetails}
            rawId={rawId}
            faxProgramCode={faxProgramCode}
            contextIsRaw={contextIsRaw}
            listQueryParams={listQueryParams}
            loggedInUserId={loggedInUserId}
            changeDisposition={changeDisposition}
            detectedDisposition={rawDetails.detectedDisposition}
            disposition={rawDetails.disposition}
            isInconclusive={rawDetails.isInconclusive}
            responseTypeList={rawDetails.responseTypeList}
            detectedDispositionContainerId={
              config.detectedDispositionContainerId
            }
          />

          <Form
            onSubmit={(event) => {
              event.preventDefault();
              submitDispositionHandler();
            }}
          >
            <DispositionFields
              formValues={formValues}
              setFieldValue={setFieldValue}
              resetDispositionFields={resetDispositionFields}
              selectableDispositions={selectableDispositions}
              rawDetails={rawDetails}
              targetOptions={rawDetails.targetOptions || []}
              dispositionFieldsConfig={config.dispositionFields}
              disableActions={disableActions}
              isRawConfirmed={isRawConfirmed}
              changeDisposition={changeDisposition}
              elementIdPrefix={config.elementIdPrefix}
            />

            {showNotes ? (
              <FaxDispositionNotes
                notesConfig={config.notes}
                value={formValues.notes}
                onChange={(event) => setFieldValue("notes", event.target.value)}
                disabled={notesDisabled}
              />
            ) : null}

            <FaxDispositionActions
              actionsLayout={actionsLayout}
              rawDetails={rawDetails}
              rawId={rawId}
              unlinkConfig={getUnlinkDeclinedFaxConfig(faxProgramCode)}
              formValues={formValues}
              contextIsRaw={contextIsRaw}
              changeDisposition={changeDisposition}
              disableActions={disableActions}
              submittingDisposition={submittingDisposition}
              selectableDispositions={selectableDispositions}
              onSubmitDisposition={submitDispositionHandler}
              onSubmitPushToPharmacy={handleSubmitPushToPharmacy}
              onSubmitForReview={handleSubmitForReview}
              onUnclaimRaw={handleUnclaimRaw}
              elementIdPrefix={config.elementIdPrefix}
            />
          </Form>
        </Col>

        <Col md={7}>
          <ConfigurablePdfViewer
            height="85vh"
            width="100%"
            url={config.getPdfUrl(rawDetails)}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default FaxDisposition;
